import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { passwordRegExp } from 'constants/passwordRegExp';
import { Form, Formik } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiConfetti, PiFingerprintSimple } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfirmEmail } from 'services/auth/confirmEmail';
import { IConfirmEmailWithPasswordForm } from 'services/auth/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { object, ref, string } from 'yup';

const confirmEmailValidationSchema = () =>
  object().shape({
    password: string()
      .matches(passwordRegExp, i18next.t('formik-error.invalid-password'))
      .required(i18next.t('formik-error.required')),
    passwordCopy: string()
      .oneOf([ref('password'), ''], i18next.t('formik-error.unmatch-password'))
      .required(i18next.t('formik-error.required')),
  });

const ConfirmEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const email = params.get('email') ?? '';
  const token = params.get('token')?.replaceAll(' ', '+') ?? '';

  const confirmEmail = useConfirmEmail();
  const goToLogin = () => navigate(`/login?email=${email}`);

  const onConfirmEmail = async (values: IConfirmEmailWithPasswordForm) => {
    await confirmEmail.mutateAsync(values, {
      onError: error => onErrorShowAllErrorsToast(error),
      onSuccess: goToLogin,
    });
  };

  return (
    <PublicPage
      header={
        <div className='flex gap-4  items-center'>
          <PiConfetti size={50} />
          <p className='text-lg font-semibold'>
            {t('confirm-email.creative.header')}
          </p>
        </div>
      }>
      <div className='w-72 opacity-65'>
        {t('reset-password.password-reg-exp')}
      </div>
      <Formik
        validateOnBlur
        validationSchema={confirmEmailValidationSchema}
        initialValues={{ email, token, password: '', passwordCopy: '' }}
        enableReinitialize
        onSubmit={onConfirmEmail}>
        <Form>
          <FormInput.Text
            name='password'
            type='password'
            label={t('input.label.password')}
            appBackground
          />
          <FormInput.Text
            name='passwordCopy'
            type='password'
            label={t('input.label.confirm-password')}
            appBackground
          />
          <div />
          <div />
          <div />
          <Button
            type='submit'
            variant={BUTTON_VARIANT.submit}
            isLoading={confirmEmail.isPending}
            fullWidth>
            <Button.Text text={t('confirm-email.button')} />
            <Button.Icon icon={<PiFingerprintSimple />} />
          </Button>
        </Form>
      </Formik>
    </PublicPage>
  );
};

export default ConfirmEmail;
